<div
  class="flex flex-col gap-1 py-2"
  [class.items-end]="message().role === ChatMessageRole.USER"
>
  <!-- Attached resources (images/PDFs) - full width -->
  <app-message-attachments-preview
    [resources]="message().resources || []"
    [alignEnd]="message().role === ChatMessageRole.USER"
  />

  <!-- Message content wrapper -->
  <div
    class="flex flex-col gap-1 min-w-0"
    [class.max-w-[75%]]="message().role === ChatMessageRole.USER"
    [class.items-end]="message().role === ChatMessageRole.USER"
  >
    <!-- Text content -->
    @if (message().content) {
      <div
        class="break-words"
        [ngClass]="{
          'px-4 py-3 bg-violet-100 text-gray-900 rounded-2xl rounded-br-sm':
            message().role === ChatMessageRole.USER,
          'text-gray-900 markdown-content':
            message().role === ChatMessageRole.ASSISTANT
        }"
      >
        @if (message().role === ChatMessageRole.USER) {
          <p
            class="m-0 text-sm sm:text-[0.9375rem] leading-relaxed whitespace-pre-wrap"
          >
            {{ message().content }}
          </p>
        } @else {
          <markdown
            class="text-sm sm:text-[0.9375rem] leading-relaxed"
            [data]="processedContent()"
          ></markdown>
        }
      </div>
    }

    <!-- Materials Analysis Preview -->
    @if (materialsAnalysisState().loading) {
      <div class="flex items-center gap-2 text-gray-500 py-3 px-4 bg-gray-50 rounded-lg">
        <lucide-icon [img]="icons.Loader2" class="w-4 h-4 animate-spin"></lucide-icon>
        <span class="text-sm">Loading materials analysis...</span>
      </div>
    } @else if (materialsAnalysisState().error) {
      <div class="flex items-center gap-2 text-red-600 py-3 px-4 bg-red-50 rounded-lg">
        <lucide-icon [img]="icons.AlertCircle" class="w-4 h-4"></lucide-icon>
        <span class="text-sm flex-1">{{ materialsAnalysisState().error }}</span>
        <button
          type="button"
          (click)="retryFetch('materials')"
          class="text-red-600 hover:text-red-800 transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.RefreshCw" class="w-4 h-4 pointer-events-none"></lucide-icon>
        </button>
      </div>
    } @else if (materialsAnalysisPreview()) {
      <app-materials-analysis-preview [preview]="materialsAnalysisPreview()!" />
    }

    <!-- Scoring Guide Preview -->
    @if (scoringGuideState().loading) {
      <div class="flex items-center gap-2 text-gray-500 py-3 px-4 bg-gray-50 rounded-lg">
        <lucide-icon [img]="icons.Loader2" class="w-4 h-4 animate-spin"></lucide-icon>
        <span class="text-sm">Loading scoring guide...</span>
      </div>
    } @else if (scoringGuideState().error) {
      <div class="flex items-center gap-2 text-red-600 py-3 px-4 bg-red-50 rounded-lg">
        <lucide-icon [img]="icons.AlertCircle" class="w-4 h-4"></lucide-icon>
        <span class="text-sm flex-1">{{ scoringGuideState().error }}</span>
        <button
          type="button"
          (click)="retryFetch('scoring')"
          class="text-red-600 hover:text-red-800 transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.RefreshCw" class="w-4 h-4 pointer-events-none"></lucide-icon>
        </button>
      </div>
    } @else if (scoringGuidePreview()) {
      <app-scoring-guide-preview [preview]="scoringGuidePreview()!" />
    }

    <!-- Grading Results Preview -->
    @if (gradingRunState().loading) {
      <div class="flex items-center gap-2 text-gray-500 py-3 px-4 bg-gray-50 rounded-lg">
        <lucide-icon [img]="icons.Loader2" class="w-4 h-4 animate-spin"></lucide-icon>
        <span class="text-sm">Loading grading results...</span>
      </div>
    } @else if (gradingRunState().error) {
      <div class="flex items-center gap-2 text-red-600 py-3 px-4 bg-red-50 rounded-lg">
        <lucide-icon [img]="icons.AlertCircle" class="w-4 h-4"></lucide-icon>
        <span class="text-sm flex-1">{{ gradingRunState().error }}</span>
        <button
          type="button"
          (click)="retryFetch('grading')"
          class="text-red-600 hover:text-red-800 transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.RefreshCw" class="w-4 h-4 pointer-events-none"></lucide-icon>
        </button>
      </div>
    } @else if (gradingResultsPreview()) {
      <app-grading-results-preview [preview]="gradingResultsPreview()!" />
    }

    <!-- Loading indicator for streaming messages -->
    @if (isLoading()) {
      <div class="flex items-center gap-2 text-gray-500 py-1 w-full">
        <lucide-icon
          [img]="icons.Loader2"
          class="w-4 h-4 animate-spin"
        ></lucide-icon>
        @if (activeToolNames().length > 0) {
          <span class="text-sm">
            Running {{ activeToolNames().join(', ') }}...
          </span>
        } @else {
          <span class="text-sm">Thinking...</span>
        }
        <span class="text-xs text-gray-400 ml-auto">{{ liveTimerDisplay() }}</span>
      </div>
    }

    <!-- Time and copy button row -->
    <div class="flex items-center gap-2">
      <span class="text-xs text-gray-400">
        {{ formatTime(message().createdAt) }}
        @if (generationDurationDisplay()) {
          <span class="text-gray-400">({{ generationDurationDisplay() }})</span>
        }
      </span>
      @if (message().role === ChatMessageRole.ASSISTANT && !isLoading()) {
        <button
          type="button"
          (click)="copyMessage()"
          class="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          [title]="copied() ? 'Copied!' : 'Copy message'"
        >
          <lucide-icon
            [img]="copied() ? icons.Check : icons.Copy"
            class="w-3.5 h-3.5 pointer-events-none"
          ></lucide-icon>
        </button>
      }
    </div>
  </div>
</div>
